import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, PauseIcon, PlayIcon, ArrowPathIcon, ChartBarIcon, Cog6ToothIcon } from '@heroicons/react/24/solid';
import { Helmet } from 'react-helmet-async';
import InfoSection from './components/InfoSection';
import TestimonialsSection from './components/TestimonialsSection';
import ComparisonSection from './components/ComparisonSection';

interface Task {
  id: string;
  title: string;
  completed: boolean;
  pomodoros: number;
}

interface TimerSettings {
  pomodoro: number;
  shortBreak: number;
  longBreak: number;
}

const DEFAULT_TIMER_SETTINGS: TimerSettings = {
  pomodoro: 25,
  shortBreak: 5,
  longBreak: 15
};

function App() {
  const [timeLeft, setTimeLeft] = useState(DEFAULT_TIMER_SETTINGS.pomodoro * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [mode, setMode] = useState<'pomodoro' | 'shortBreak' | 'longBreak'>('pomodoro');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showStats, setShowStats] = useState(false);
  const [pomodorosCompleted, setPomodorosCompleted] = useState(0);
  const [currentTask, setCurrentTask] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [timerSettings, setTimerSettings] = useState<TimerSettings>(DEFAULT_TIMER_SETTINGS);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio and check notification permission on mount
  useEffect(() => {
    // Initialize audio
    const audio = new Audio('/notification.mp3');
    audio.volume = volume;
    audioRef.current = audio;

    // Check notification permission
    const checkNotificationPermission = async () => {
      try {
        if (!('Notification' in window)) {
          console.log('This browser does not support notifications');
          return;
        }

        // If already granted, update state
        if (Notification.permission === 'granted') {
          setNotificationsEnabled(true);
        }
        // If not denied, request permission
        else if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          setNotificationsEnabled(permission === 'granted');
          
          // Show test notification if granted
          if (permission === 'granted') {
            new Notification('Pomodoro Timer', {
              body: 'Notifications are enabled! You will be notified when your timer completes.',
              icon: '/timer.svg'
            });
            // Also test sound
            audio.play().catch(console.error);
          }
        }
      } catch (error) {
        console.error('Error checking notification permission:', error);
      }
    };

    checkNotificationPermission();

    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [volume]);

  // Update audio volume when volume setting changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const playNotificationSound = useCallback(async () => {
    try {
      if (audioRef.current) {
        // Reset the audio to the beginning
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      } else {
        // Fallback if audio ref is not set
        const audio = new Audio('/notification.mp3');
        audio.volume = volume;
        await audio.play();
      }
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }, [volume]);

  const showNotification = useCallback((title: string, body: string) => {
    try {
      if (!('Notification' in window)) {
        console.log('This browser does not support notifications');
        return;
      }

      if (Notification.permission === 'granted') {
        const notification = new Notification(title, {
          body,
          icon: '/timer.svg',
          badge: '/timer.svg',
          silent: true, // We'll handle the sound separately
          requireInteraction: false
        });

        // Auto close after 5 seconds
        setTimeout(() => notification.close(), 5000);
      }
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }, []);

  const handleTimerComplete = useCallback(() => {
    const message = mode === 'pomodoro' 
      ? 'Time to take a break!' 
      : 'Break is over, back to work!';

    // Play sound first (don't wait for it to complete)
    playNotificationSound();
    
    // Show notification
    if (notificationsEnabled) {
      showNotification('Pomodoro Timer', message);
    }
    
    // Update statistics
    if (mode === 'pomodoro') {
      setPomodorosCompleted(prev => prev + 1);
      if (currentTask) {
        setTasks(prev => prev.map(task => 
          task.id === currentTask 
            ? { ...task, pomodoros: task.pomodoros + 1 }
            : task
        ));
      }
    }
    
    setIsRunning(false);
  }, [mode, currentTask, notificationsEnabled, playNotificationSound, showNotification]);

  const requestNotificationPermission = async () => {
    try {
      if (!('Notification' in window)) {
        alert('This browser does not support notifications');
        return;
      }

      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
      
      if (permission === 'granted') {
        // Test notification
        showNotification(
          'Pomodoro Timer', 
          'Notifications are enabled! You will be notified when your timer completes.'
        );
        // Test sound
        playNotificationSound();
      } else if (permission === 'denied') {
        alert('Please enable notifications in your browser settings to receive timer alerts.');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      alert('There was an error enabling notifications. Please try again.');
    }
  };

  const toggleTimer = () => setIsRunning(!isRunning);

  const resetTimer = () => {
    setIsRunning(false);
    switch (mode) {
      case 'pomodoro':
        setTimeLeft(timerSettings.pomodoro * 60);
        break;
      case 'shortBreak':
        setTimeLeft(timerSettings.shortBreak * 60);
        break;
      case 'longBreak':
        setTimeLeft(timerSettings.longBreak * 60);
        break;
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const addTask = () => {
    const taskTitle = prompt('Enter task name:');
    if (taskTitle?.trim()) {
      setTasks(prev => [...prev, {
        id: Date.now().toString(),
        title: taskTitle,
        completed: false,
        pomodoros: 0
      }]);
    }
  };

  const toggleTaskCompletion = (taskId: string) => {
    setTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };

  const handleTimerSettingChange = (setting: keyof TimerSettings, value: string) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue > 0) {
      setTimerSettings(prev => ({
        ...prev,
        [setting]: numValue
      }));
      // Update current timer if we're changing the current mode's duration
      if (mode === setting) {
        setTimeLeft(numValue * 60);
      }
    }
  };

  const progress = ((mode === 'pomodoro' ? timerSettings.pomodoro * 60 : mode === 'shortBreak' ? timerSettings.shortBreak * 60 : timerSettings.longBreak * 60) - timeLeft) / (mode === 'pomodoro' ? timerSettings.pomodoro * 60 : mode === 'shortBreak' ? timerSettings.shortBreak * 60 : timerSettings.longBreak * 60) * 100;

  useEffect(() => {
    let interval: number | undefined;

    if (isRunning && timeLeft > 0) {
      interval = window.setInterval(() => {
        setTimeLeft((time) => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleTimerComplete();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, timeLeft, handleTimerComplete]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex flex-col items-center justify-start p-2 sm:p-4">
      <Helmet>
        <title>{`${formatTime(timeLeft)} - Pomodoro Timer`}</title>
        <meta name="description" content="A premium Pomodoro Timer application to boost your productivity" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <meta name="theme-color" content="#6366f1" />
      </Helmet>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 sm:p-8 w-full max-w-4xl shadow-2xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8">
          <div className="md:col-span-2">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 sm:mb-8">
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 sm:gap-4">
                {['pomodoro', 'shortBreak', 'longBreak'].map((m) => (
                  <motion.button
                    key={m}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setMode(m as typeof mode);
                      setTimeLeft(m === 'pomodoro' ? timerSettings.pomodoro * 60 : m === 'shortBreak' ? timerSettings.shortBreak * 60 : timerSettings.longBreak * 60);
                      setIsRunning(false);
                    }}
                    className={`px-3 py-2 sm:px-4 sm:py-2 text-sm sm:text-base rounded-lg transition-colors ${
                      mode === m ? 'bg-white text-purple-600 shadow-lg' : 'text-white hover:bg-white/20'
                    }`}
                  >
                    {m === 'pomodoro' ? 'Pomodoro' : m === 'shortBreak' ? 'Short Break' : 'Long Break'}
                  </motion.button>
                ))}
              </div>
              <div className="flex gap-2 sm:gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowStats(!showStats)}
                  className="p-2 rounded-lg text-white hover:bg-white/20"
                >
                  <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg text-white hover:bg-white/20"
                >
                  <Cog6ToothIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </motion.button>
              </div>
            </div>

            <div className="relative mb-8">
              <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                  className="h-full bg-white rounded-full"
                />
              </div>
            </div>

            <div className="text-center mb-6 sm:mb-8">
              <motion.h1
                key={timeLeft}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="text-6xl sm:text-8xl font-bold text-white mb-4 font-mono"
              >
                {formatTime(timeLeft)}
              </motion.h1>
            </div>

            <div className="flex justify-center gap-4 sm:gap-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTimer}
                className="bg-white text-purple-600 rounded-full p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                {isRunning ? (
                  <PauseIcon className="h-6 w-6 sm:h-8 sm:w-8" />
                ) : (
                  <PlayIcon className="h-6 w-6 sm:h-8 sm:w-8" />
                )}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={resetTimer}
                className="bg-white/20 text-white rounded-full p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <ArrowPathIcon className="h-6 w-6 sm:h-8 sm:w-8" />
              </motion.button>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Tasks</h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={addTask}
                className="px-3 py-1 bg-white/20 text-white rounded-lg hover:bg-white/30"
              >
                Add Task
              </motion.button>
            </div>

            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {tasks.map((task) => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    currentTask === task.id ? 'bg-white/20' : 'bg-white/10'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={task.completed}
                      onChange={() => toggleTaskCompletion(task.id)}
                      className="rounded border-white/30 text-purple-600 focus:ring-purple-500"
                    />
                    <span className={`text-white ${task.completed ? 'line-through opacity-50' : ''}`}>
                      {task.title}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-white/70 text-sm">{task.pomodoros} 🍅</span>
                    <button
                      onClick={() => setCurrentTask(task.id)}
                      className={`px-2 py-1 rounded text-xs ${
                        currentTask === task.id
                          ? 'bg-white/30 text-white'
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      Focus
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <AnimatePresence>
          {showStats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="mt-8 p-6 bg-white/5 rounded-2xl"
            >
              <h2 className="text-xl font-semibold text-white mb-4">Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/10 rounded-xl p-4">
                  <h3 className="text-white/70 text-sm">Pomodoros Completed</h3>
                  <p className="text-2xl font-bold text-white">{pomodorosCompleted}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <h3 className="text-white/70 text-sm">Total Focus Time</h3>
                  <p className="text-2xl font-bold text-white">
                    {Math.floor(pomodorosCompleted * 25)} mins
                  </p>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <h3 className="text-white/70 text-sm">Tasks Completed</h3>
                  <p className="text-2xl font-bold text-white">
                    {tasks.filter(t => t.completed).length}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="mt-6 sm:mt-8 p-4 sm:p-6 bg-white/5 rounded-2xl"
            >
              <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Settings</h2>
              <div className="space-y-6">
                <div>
                  <label className="text-white/70 text-sm block mb-2">
                    Notification Volume
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <h3 className="text-white/70 text-sm mb-4">Timer Duration (minutes)</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="text-white/70 text-sm block mb-2">
                        Pomodoro
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={timerSettings.pomodoro}
                        onChange={(e) => handleTimerSettingChange('pomodoro', e.target.value)}
                        className="w-full bg-white/10 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/50"
                      />
                    </div>
                    <div>
                      <label className="text-white/70 text-sm block mb-2">
                        Short Break
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={timerSettings.shortBreak}
                        onChange={(e) => handleTimerSettingChange('shortBreak', e.target.value)}
                        className="w-full bg-white/10 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/50"
                      />
                    </div>
                    <div>
                      <label className="text-white/70 text-sm block mb-2">
                        Long Break
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={timerSettings.longBreak}
                        onChange={(e) => handleTimerSettingChange('longBreak', e.target.value)}
                        className="w-full bg-white/10 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/50"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-white/70 text-sm mb-4">Notifications</h3>
                  <div className="space-y-2">
                    <button
                      onClick={requestNotificationPermission}
                      className={`w-full px-4 py-2 rounded-lg ${
                        notificationsEnabled
                          ? 'bg-green-500/20 text-green-300'
                          : 'bg-white/20 text-white hover:bg-white/30'
                      }`}
                    >
                      {notificationsEnabled ? (
                        <span className="flex items-center justify-center gap-2">
                          Notifications Enabled
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      ) : (
                        'Enable Notifications'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        playNotificationSound();
                        if (notificationsEnabled) {
                          showNotification('Test Notification', 'This is a test notification!');
                        }
                      }}
                      className="w-full px-4 py-2 rounded-lg bg-white/20 text-white hover:bg-white/30"
                    >
                      Test Notifications
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <TestimonialsSection />
      <ComparisonSection />
      <InfoSection />
      <div className="w-full text-center py-6 text-white/70">
        <a 
          href="https://www.imagemaths.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="hover:text-white transition-colors"
        >
          Visit ImageMaths.com
        </a>
      </div>
    </div>
  );
}

export default App;
